import * as Tooltip from "@radix-ui/react-tooltip";
import classNames from "classnames";
import { Form, Formik } from "formik";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { FC, useState } from "react";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { PostHogEvent } from "@/shared/types";
import { Button, Input, Typography } from "@/shared/ui";
import { useOnboardingStore } from "@/stores";
import AwardIcon from "@/svgs/award.svg";
import InfoIcon from "@/svgs/info.svg";
import TrashIcon from "@/svgs/trash.svg";
import { UserDetailedInfo } from "@/types";

import styles from "./accomplishments.module.scss";
import { AccomplishmentsFormScheme } from "./accomplishments.types";
import { useCompleteOnboarding } from "./hooks/complete-onboarding.hook";

const validationScheme = object({
  accomplishmentForm: object({
    description: string().max(100, "Must be at most 100 characters"),
    link: string().url("URL is invalid"),
  }),
});

const AccomplishmentsTab: FC = () => {
  const posthog = usePostHog();
  const { isPending, mutate } = useCompleteOnboarding();

  const userInfo = useOnboardingStore((state) => state.userInfo);
  const currentStep = useOnboardingStore((state) => state.currentStep);
  const userImageFormData = useOnboardingStore((state) => state.userImageFormData);
  const { setInfo, addPassedStep } = useOnboardingStore((state) => state.actions);

  const [isHintOpened, setIsHintOpened] = useState(false);

  const isFirstPassingStep = useOnboardingStore((state) => !state.passedSteps.includes(currentStep));

  const onSubmitHandler = (data: AccomplishmentsFormScheme) => {
    let accomplishments: AccomplishmentsFormScheme["accomplishments"];

    if (data.accomplishments) {
      accomplishments = data.accomplishments.map((accomplishment) => ({
        ...accomplishment,
        link: accomplishment.link,
      }));

      setInfo({ accomplishments });
    }
    if (isFirstPassingStep) {
      posthog?.capture(PostHogEvent.DESCRIPTION_ADDED);
    }

    if (userImageFormData) {
      mutate({
        userDetailedInfo: {
          ...userInfo,
          accomplishments,
        } as UserDetailedInfo,
        profileImageFormData: userImageFormData,
      });
    }
    addPassedStep(currentStep);
  };

  return (
    <main className={styles.accomplishmentsTab}>
      <div className={styles.title}>
        <Typography semi variant="title-2">
          Add accomplishments
        </Typography>

        <Tooltip.Provider>
          <Tooltip.Root open={isHintOpened} onOpenChange={(v) => setIsHintOpened(v)}>
            <Tooltip.Trigger asChild>
              <button type="button" onClick={() => setIsHintOpened((prev) => !prev)}>
                <InfoIcon className={styles.infoIcon} />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content align="center" className={styles.tooltip} sideOffset={4}>
                Share your accomplishments with Sociètè members.
                <br /> For example:
                <ul>
                  <li>- Modeling portfolio</li>
                  <li>- Featured articles</li>
                  <li>- Brand ambassadorship</li>
                  <li>- Company founder</li>
                  <li>- Keynote speaker</li>
                </ul>
                <Tooltip.Arrow className={styles.Arrow} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </div>

      <Formik<AccomplishmentsFormScheme>
        initialValues={{
          accomplishments: userInfo.accomplishments ?? [],
          accomplishmentForm: {
            description: "",
            link: "",
          },
        }}
        validationSchema={toFormikValidationSchema(validationScheme)}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={onSubmitHandler}
      >
        {({ values, errors, setFieldValue, validateField }) => (
          <Form className={styles.form}>
            <Input
              placeholder="Description"
              value={values.accomplishmentForm.description}
              message={errors.accomplishmentForm?.description}
              variant={errors.accomplishmentForm?.description ? "error" : undefined}
              onBlur={() => validateField("accomplishmentForm.description")}
              onValueChange={async (value) => {
                await setFieldValue("accomplishmentForm.description", value);
                values.accomplishmentForm.link && (await validateField("accomplishmentForm.description"));
              }}
            />
            <Input
              placeholder="Link"
              value={values.accomplishmentForm.link}
              message={errors.accomplishmentForm?.link}
              variant={errors.accomplishmentForm?.link ? "error" : undefined}
              onBlur={() => validateField("accomplishmentForm.link")}
              onValueChange={async (value) => {
                await setFieldValue("accomplishmentForm.link", value);
                await validateField("accomplishmentForm.link");
              }}
            />
            <Button
              variant="outlined"
              disabled={
                !values.accomplishmentForm.description ||
                !values.accomplishmentForm?.link ||
                !!errors.accomplishmentForm?.link
              }
              onClick={() => {
                if (!values.accomplishments) return;

                const newAccomplishments = [
                  ...values.accomplishments,
                  { description: values.accomplishmentForm.description, link: values.accomplishmentForm.link },
                ];
                setFieldValue("accomplishments", newAccomplishments);
                setInfo({ accomplishments: newAccomplishments });

                setFieldValue("accomplishmentForm.description", "");
                setFieldValue("accomplishmentForm.link", "");
              }}
            >
              Add
            </Button>

            {values.accomplishments?.map(({ description, link }, index) => (
              <div key={index} className={styles.accomplishment}>
                <div className={styles.left}>
                  <AwardIcon className={styles.icon} />
                  <Link href={link ?? ""} target="_blank" className={styles.link}>
                    <Typography variant="caption-1">{description}</Typography>
                  </Link>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFieldValue(
                      "accomplishments",
                      values.accomplishments?.filter((accomplishment) => accomplishment.description !== description)
                    )
                  }
                >
                  <TrashIcon className={classNames(styles.icon, styles.trashIcon)} />
                </button>
              </div>
            ))}
            <Button
              fullWidth
              size="big"
              type="button"
              disabled={isPending || !values.accomplishments?.length}
              onClick={() => onSubmitHandler(values)}
            >
              {isPending ? "Loading..." : "Continue"}
            </Button>
            <Button
              fullWidth
              size="big"
              variant="skip1"
              type="button"
              disabled={isPending}
              onClick={() => onSubmitHandler(values)}
            >
              {isPending ? "Loading..." : "Skip"}
            </Button>
          </Form>
        )}
      </Formik>
    </main>
  );
};

export { AccomplishmentsTab };
